import { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate, Link } from "react-router-dom";
import {
  AlertCircle, ArrowLeft, BarChart3, Bell, Bookmark, BookmarkCheck, Calendar, Check,
  Copy, Download, Edit, ExternalLink, Inbox, MessageSquare, MoreHorizontal, Plus, RefreshCw, Sparkles, TrendingUp, X,
} from "lucide-react";
import {
  useBloggers, useKanban, useScalePlacement, useCollections, useCollection,
  useMembership, useAddBloggersToCollection,
  useExtendCampaignFromCollection, useConfirmPlacement,
  useApproveScript, useRequestScriptChanges, useCancelPlacement,
  type Blogger, type KanbanResponse, type Membership, type Placement, type CollectionSummary,
} from "@/shared/api/wb";
import { useDraftStore } from "@/shared/store/draft";
import { Avatar, Card, Chip, PlatformIcon, Segmented, Skeleton, Toast, type Platform } from "@/shared/ui";
import { AddToCollectionModal } from "@/shared/ui/AddToCollectionModal";
import { fmtDate, fmtNum, fmtRub, fmtRubFull } from "@/shared/lib/format";
import { cn } from "@/shared/lib/cn";
import { ExpansionPanel } from "./CampaignKanban/ExpansionPanel";
import { ReplacementModal } from "./CampaignKanban/ReplacementModal";
import { CreativesDrawer } from "./CampaignKanban/CreativesDrawer";
import { ExportModal } from "./CampaignKanban/ExportModal";
import { ExpansionRail } from "./CampaignKanban/ExpansionRail";
import { PlacementDetailModal } from "./CampaignKanban/PlacementDetailModal";
import { AddChannelsPanel } from "./CampaignKanban/AddChannelsPanel";
import { InboxModal } from "./InboxModal";
import { WriteToModal } from "./WriteToModal";

const FILTERS = ["Все", "Новые", "Telegram", "YouTube", "Требуют замены"];

export function CampaignKanban() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useKanban(id ?? null);
  const scaleP = useScalePlacement(id ?? "");
  const confirmP = useConfirmPlacement(id ?? "");
  const approveScriptP = useApproveScript(id ?? "");
  const requestScriptChangesP = useRequestScriptChanges(id ?? "");
  const cancelP = useCancelPlacement(id ?? "");
  const { branch, setBranch } = useDraftStore();
  const { data: bloggers } = useBloggers({});
  const byId = new Map((bloggers ?? []).map((b) => [b.id, b]));
  const { data: membership } = useMembership();
  const [filter, setFilter] = useState(FILTERS[0]);
  const [expansionOpen, setExpansionOpen] = useState(false);
  const [addChannelsOpen, setAddChannelsOpen] = useState(false);
  const [addChannelsPreselect, setAddChannelsPreselect] = useState<string[]>([]);
  const [addChannelsPanelTitle, setAddChannelsPanelTitle] = useState<string | undefined>(undefined);
  const [replacement, setReplacement] = useState<Placement | null>(null);
  const [creativesOpen, setCreativesOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [detail, setDetail] = useState<Placement | null>(null);
  const [saveToCol, setSaveToCol] = useState<{ id: string; name: string } | null>(null);
  const [fromColOpen, setFromColOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [inboxBloggerId, setInboxBloggerId] = useState<string | null>(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [scriptChangesFor, setScriptChangesFor] = useState<Placement | null>(null);
  // ?launched=1 from PaymentStep → celebrate, then strip the param so a refresh
  // doesn't re-show the modal.
  const location = useLocation();
  const justLaunched = new URLSearchParams(location.search).get("launched") === "1";
  const [showLaunched, setShowLaunched] = useState(justLaunched);
  useEffect(() => {
    if (justLaunched) {
      const u = new URLSearchParams(location.search);
      u.delete("launched");
      navigate({ search: u.toString() }, { replace: true });
    }
  }, [justLaunched, location.search, navigate]);

  if (isLoading || !data) return <KanbanSkeleton />;

  const existingBloggerIds = [...new Set(data.placements.map((p) => p.blogger_id))];

  function openSimilar() {
    const similar = (bloggers ?? [])
      .filter((b) => !existingBloggerIds.includes(b.id))
      .slice(0, 4)
      .map((b) => b.id);
    setAddChannelsPreselect(similar);
    setAddChannelsPanelTitle("Похожие каналы");
    setAddChannelsOpen(true);
  }

  function openCatalogFromRecs(bloggerIds: string[]) {
    setExpansionOpen(false);
    setAddChannelsPreselect(bloggerIds);
    setAddChannelsPanelTitle("AI-рекомендации");
    setAddChannelsOpen(true);
  }

  function confirmAndPay(placementId: string, bloggerName: string) {
    confirmP.mutate(placementId, {
      onSuccess: (res) => setToastMsg(`${bloggerName}: подтверждено, ${fmtRubFull(res.paid)} зачислено на баланс блогера`),
    });
  }

  function approveScript(placementId: string, bloggerName: string) {
    approveScriptP.mutate(placementId, {
      onSuccess: () => setToastMsg(`${bloggerName}: сценарий согласован, ждём публикации`),
    });
  }

  function cancelPlacement(placementId: string, bloggerName: string) {
    cancelP.mutate({ placementId }, {
      onSuccess: () => setToastMsg(`${bloggerName}: размещение отменено, бюджет возвращён на баланс`),
    });
  }

  const closedCount = data.placements.filter((p) => p.result).length;
  const total = data.placements.length;
  const totalConv = data.placements.reduce((s, p) => s + ((p.result?.conv) ?? 0), 0);
  const avgCpv = data.scale_banner?.avg_cpv ?? 0;

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="px-7 pt-7 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2.5 mb-2">
            <Link to="/account/projects" className="inline-flex items-center gap-1 text-[12px] text-ink-40 hover:text-ink">
              <ArrowLeft size={13} /> Мои РК
            </Link>
            <span className="text-ink-12">/</span>
            <TypeBadge type={data.campaign.type} />
            <span className="w-2 h-2 rounded-full bg-lime animate-pulse" />
            <span className="text-[12px] text-ink-40">активна</span>
          </div>
          <h1 className="font-display font-extrabold text-[28px] tracking-tight">{data.campaign.product}</h1>
        </div>
        <div className="flex items-center gap-2.5">
          <HeaderBtn icon={<Copy size={14} />} onClick={() => setCreativesOpen(true)}>
            Креативы · <span className="font-mono text-purple-900">{data.campaign.creatives_count || 2}</span>
          </HeaderBtn>
          <HeaderBtn icon={<Inbox size={14} />} onClick={() => setInboxOpen(true)}>Лента чатов</HeaderBtn>
          <div className="relative">
            <button onClick={() => setMoreOpen((v) => !v)}
              className="inline-flex items-center justify-center h-9 w-9 rounded-pill border-2 border-ink-12 text-ink hover:border-ink/40">
              <MoreHorizontal size={16} />
            </button>
            {moreOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMoreOpen(false)} />
                <div className="absolute right-0 top-11 z-20 w-52 rounded-lg border border-ink-12 bg-white shadow-lg py-1.5 fade-up">
                  <MoreMenuItem icon={<Bookmark size={14} />} onClick={() => { setMoreOpen(false); setFromColOpen(true); }}>
                    Из подборки
                  </MoreMenuItem>
                  <MoreMenuItem icon={<BarChart3 size={14} />} onClick={() => { setMoreOpen(false); navigate(`/analytics?campaign=${id}`); }}>
                    Аналитика
                  </MoreMenuItem>
                  <MoreMenuItem icon={<Download size={14} />} onClick={() => { setMoreOpen(false); setExportOpen(true); }}>
                    Выгрузить итоги
                  </MoreMenuItem>
                </div>
              </>
            )}
          </div>
          <button onClick={() => { setAddChannelsPreselect([]); setAddChannelsPanelTitle(undefined); setAddChannelsOpen(true); }}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-semibold text-[13px] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]">
            <Plus size={14} strokeWidth={3} /> Добавить каналы
          </button>
          <button onClick={() => setExpansionOpen(true)}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-pill border border-ink-12 font-display font-semibold text-[13px] text-ink-60 hover:text-ink hover:border-ink/40">
            <Sparkles size={14} /> AI-рекомендации
          </button>
        </div>
      </div>

      {/* LIVE strip */}
      <div className="mx-7 mt-5 p-4 rounded-lg bg-ink text-white grid grid-cols-[auto_1fr_auto] gap-6 items-center">
        <div className="flex items-center gap-3">
          <span className="w-2.5 h-2.5 rounded-full bg-lime animate-pulse" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.08em] font-semibold opacity-60">Live · обновлено сейчас</div>
            <div className="font-display font-extrabold text-[22px] tracking-tight mt-0.5">
              {closedCount} из {total} завершено
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-5">
          <LiveStat label="Просмотры" val={fmtNum(data.metrics.live_views)} accent />
          <LiveStat label="Клики"     val={fmtNum(data.metrics.live_clicks)} />
          <LiveStat label="Конверсии" val={String(totalConv)} />
          <LiveStat label="CPV"       val={`${avgCpv} ₽`} />
        </div>
        <div className="text-right text-[12px] opacity-70 leading-tight">
          <div>Потрачено</div>
          <div className="font-mono text-[14px] font-bold text-lime mt-1 opacity-100">{fmtRubFull(data.metrics.spent)}</div>
        </div>
      </div>

      {/* Proactive scale banner */}
      {data.scale_banner && (
        <ProactiveScaleBanner
          banner={data.scale_banner}
          bloggerName={byId.get(data.scale_banner.blogger_id)?.name ?? ""}
          winnerCount={data.placements.filter((p) => p.top_performer || (p.result && p.result.cpv < 0.5)).length}
          pending={total - closedCount}
          onExpand={() => setExpansionOpen(true)}
          onExport={() => setExportOpen(true)}
        />
      )}

      {/* View toggle + filter chips (kanban view chip-row also hosts the toggle so
          it lives next to the actual board it switches between). */}
      <div className="flex items-center gap-2 px-7 pt-4 pb-2 flex-wrap">
        <Segmented<"kanban" | "table"> value={branch.kanbanView} onChange={(v) => setBranch("kanbanView", v)}
          options={[{ value: "table", label: "Таблица" }, { value: "kanban", label: "Канбан" }]} />
        {branch.kanbanView === "kanban" && (
          <div className="flex gap-2 flex-wrap ml-3">
            {FILTERS.map((t) => (
              <Chip key={t} active={t === filter} onClick={() => setFilter(t)}>{t}</Chip>
            ))}
          </div>
        )}
      </div>

      {branch.kanbanView === "kanban"
        ? <Board cols={data.columns} placements={data.placements} bloggers={byId} onReplace={setReplacement} onScale={() => setExpansionOpen(true)} onWrite={(bid) => setInboxBloggerId(bid)} onOpenDetail={setDetail} onRepeat={(pid) => scaleP.mutate(pid)} onFindSimilar={openSimilar} onConfirm={confirmAndPay} onApproveScript={approveScript} onRequestScriptChanges={(p) => setScriptChangesFor(p)} onCancel={cancelPlacement} membership={membership ?? {}} onSaveToCollection={(bid, bname) => setSaveToCol({ id: bid, name: bname })} />
        : <TableView placements={data.placements} bloggers={byId} columns={data.columns} onOpenDetail={setDetail} onReplace={setReplacement} onRepeat={(pid) => scaleP.mutate(pid)} onFindSimilar={openSimilar} onConfirm={confirmAndPay} onApproveScript={approveScript} membership={membership ?? {}} onSaveToCollection={(bid, bname) => setSaveToCol({ id: bid, name: bname })} onWrite={(bid) => setInboxBloggerId(bid)} />}

      <ExpansionRail bloggers={byId} onAdd={(bid) => { setAddChannelsPreselect([bid]); setAddChannelsOpen(true); }} />

      {addChannelsOpen && id && (
        <AddChannelsPanel
          campaignId={id}
          campaignProduct={data.campaign.product}
          campaignType={data.campaign.type}
          existingBloggerIds={existingBloggerIds}
          initialSelected={addChannelsPreselect}
          panelTitle={addChannelsPanelTitle}
          onClose={() => { setAddChannelsOpen(false); setAddChannelsPreselect([]); setAddChannelsPanelTitle(undefined); }}
          onAdded={setToastMsg}
        />
      )}
      {expansionOpen && (
        <ExpansionPanel
          onClose={() => setExpansionOpen(false)}
          onOpenCatalog={openCatalogFromRecs}
          placements={data.placements}
          bloggers={byId}
        />
      )}
      {replacement && (
        <ReplacementModal
          placement={replacement}
          bloggers={byId}
          existingBloggerIds={existingBloggerIds}
          onClose={() => setReplacement(null)}
          onReplaced={setToastMsg}
        />
      )}
      {scriptChangesFor && (
        <ScriptChangesModal
          bloggerName={byId.get(scriptChangesFor.blogger_id)?.name ?? ""}
          onClose={() => setScriptChangesFor(null)}
          onSubmit={(message) => {
            requestScriptChangesP.mutate({ placementId: scriptChangesFor.id, message }, {
              onSuccess: () => setToastMsg("Правки к сценарию отправлены блогеру"),
            });
            setScriptChangesFor(null);
          }}
        />
      )}
      {creativesOpen && id && <CreativesDrawer campaignId={id} onClose={() => setCreativesOpen(false)} />}
      {exportOpen && <ExportModal onClose={() => setExportOpen(false)} placements={data.placements} />}
      {detail && id && (
        <PlacementDetailModal
          placement={detail}
          campaignId={id}
          statusLabel={data.columns.find((c) => c.id === detail.status)?.label ?? detail.status}
          onClose={() => setDetail(null)}
          onReplace={detail.status === "declined" ? () => { setReplacement(detail); setDetail(null); } : undefined}
          onRepeat={(detail.status === "done" || detail.status === "published") ? () => { scaleP.mutate(detail.id); setDetail(null); } : undefined}
          onFindSimilar={(detail.status === "done" || detail.status === "published") ? () => { setDetail(null); openSimilar(); } : undefined}
          onConfirm={detail.status === "review" ? () => { confirmAndPay(detail.id, byId.get(detail.blogger_id)?.name ?? ""); setDetail(null); } : undefined}
          onApproveScript={detail.status === "script_review" ? () => { approveScript(detail.id, byId.get(detail.blogger_id)?.name ?? ""); setDetail(null); } : undefined}
          onRequestScriptChanges={detail.status === "script_review" ? () => { setScriptChangesFor(detail); setDetail(null); } : undefined}
          onCancel={!["done", "declined", "cancelled"].includes(detail.status) ? () => { cancelPlacement(detail.id, byId.get(detail.blogger_id)?.name ?? ""); setDetail(null); } : undefined}
        />
      )}
      {showLaunched && <LaunchSuccessModal onClose={() => setShowLaunched(false)} />}
      {saveToCol && (
        <AddToCollectionModal
          bloggerId={saveToCol.id}
          bloggerName={saveToCol.name}
          onClose={() => setSaveToCol(null)}
        />
      )}
      {fromColOpen && id && (
        <AddFromCollectionModal
          campaignId={id}
          existingBloggerIds={new Set(data.placements.map((p) => p.blogger_id))}
          onClose={() => setFromColOpen(false)}
        />
      )}
      {inboxBloggerId && byId.get(inboxBloggerId) && (
        <WriteToModal
          open
          onClose={() => setInboxBloggerId(null)}
          campaignId={id!}
          blogger={byId.get(inboxBloggerId)!}
        />
      )}
      {inboxOpen && (
        <InboxModal
          open
          onClose={() => setInboxOpen(false)}
          campaignId={id}
        />
      )}
      {toastMsg && <Toast message={toastMsg} onHide={() => setToastMsg(null)} />}
    </div>
  );
}

const BOT_URL = "https://t.me/wowblogger_bot";

const BOT_PERKS = [
  { icon: <Sparkles size={13} />, text: "Сообщение, когда выйдет каждое размещение" },
  { icon: <MessageSquare size={13} />, text: "Новые сообщения от блогеров прямо в Telegram" },
  { icon: <Bell size={13} />, text: "Напоминание, если блогер не ответил 24 ч" },
];

function LaunchSuccessModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[120] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg fade-up p-7 grid gap-5"
        style={{ width: "min(460px, 92vw)" }}>

        {/* Success header */}
        <div className="text-center grid gap-2.5">
          <div className="w-14 h-14 rounded-full bg-lime grid place-items-center mx-auto text-[var(--fg-on-accent)]">
            <Check size={26} strokeWidth={3} />
          </div>
          <h3 className="font-display font-extrabold text-[22px] tracking-tight">Кампания запущена!</h3>
          <p className="text-[13.5px] text-ink-60 leading-relaxed">
            Получаем ОРД-маркировку и передаём в ЕРИР. Размещения начнутся в&nbsp;ближайшее время.
          </p>
        </div>

        {/* Bot CTA block */}
        <div className="rounded-xl border-2 border-[#E1F5EE] bg-[#F4FBF8] p-4 grid gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex-shrink-0 grid place-items-center"
              style={{ background: "linear-gradient(135deg,#9FE1CB,#085041)" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z"/>
              </svg>
            </div>
            <div>
              <div className="font-display font-bold text-[13.5px] text-[#085041]">Подключите Telegram-бота</div>
              <div className="text-[11.5px] text-ink-40">Не пропустите ни одного события по кампании</div>
            </div>
          </div>
          <div className="grid gap-2">
            {BOT_PERKS.map((p, i) => (
              <div key={i} className="flex items-center gap-2.5 text-[12.5px] text-ink-60">
                <span className="text-[#0F6E56] flex-shrink-0">{p.icon}</span>
                {p.text}
              </div>
            ))}
          </div>
          <a
            href={BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 h-10 rounded-pill bg-[#085041] text-white font-display font-semibold text-[13.5px] hover:bg-[#063830] transition-colors"
          >
            Подключить бота <ExternalLink size={13} />
          </a>
        </div>

        <button onClick={onClose}
          className="text-[13px] text-ink-40 hover:text-ink text-center transition-colors">
          Позже
        </button>
      </div>
    </div>
  );
}

function TypeBadge({ type }: { type: "seeding" | "native" }) {
  const Icon = type === "seeding" ? Copy : Edit;
  const label = type === "seeding" ? "Посев" : "Натив";
  const bg = type === "seeding" ? "bg-lime/20" : "bg-pink/20";
  return (
    <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-pill text-[11px] font-display font-semibold text-ink", bg)}>
      <Icon size={11} strokeWidth={2.5} /> {label}
    </span>
  );
}

function HeaderBtn({ icon, children, onClick }: { icon: React.ReactNode; children: React.ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-pill border-2 border-ink-12 text-[12.5px] font-display font-semibold text-ink hover:border-ink/40 whitespace-nowrap">
      <span className="flex-shrink-0">{icon}</span>
      <span>{children}</span>
    </button>
  );
}

function MoreMenuItem({ icon, children, onClick }: { icon: React.ReactNode; children: React.ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="w-full flex items-center gap-2.5 px-3.5 h-9 text-[13px] text-ink hover:bg-ink-3">
      <span className="flex-shrink-0 text-ink-40">{icon}</span>
      <span>{children}</span>
    </button>
  );
}

function LiveStat({ label, val, accent }: { label: string; val: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.06em] font-semibold opacity-50">{label}</div>
      <div className={cn("font-display font-extrabold text-[22px] tracking-tight mt-0.5", accent && "text-lime")}>{val}</div>
    </div>
  );
}

function ProactiveScaleBanner(props: {
  banner: NonNullable<KanbanResponse["scale_banner"]>;
  bloggerName: string; winnerCount: number; pending: number;
  onExpand: () => void; onExport: () => void;
}) {
  const { banner, bloggerName, winnerCount, pending, onExpand, onExport } = props;
  return (
    <div className="mx-7 mt-3.5 p-4 rounded-lg border-[1.5px] border-lime grid grid-cols-[auto_1fr_auto] gap-5 items-center"
      style={{ background: "linear-gradient(110deg, rgba(191,249,63,0.20), var(--bg-elevated))" }}>
      <div className="w-11 h-11 rounded-md bg-lime text-[var(--fg-on-accent)] grid place-items-center flex-shrink-0">
        <TrendingUp size={22} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--fg-on-accent)] mb-1">
          Пора масштабировать · не ждите остальных
        </div>
        <div className="font-display font-extrabold text-[18px] tracking-tight mb-1">
          {winnerCount} {winnerCount === 1 ? "канал" : "канала"} уже отработали лучше плана
        </div>
        <div className="text-[13px] text-ink-60 leading-relaxed">
          <b>{bloggerName}</b> дал CPV <b className="text-success">{banner.cpv}₽</b> — в <b>{banner.times_better}×</b> дешевле среднего по кампании.
          {pending > 0 && ` Ещё ${pending} в работе, но результат уже виден — повторите и расширьте сейчас, пока ставки те же.`}
        </div>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <button onClick={onExport}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-pill text-[13px] font-display font-semibold text-ink-60 hover:text-ink hover:bg-ink-6">
          <Download size={14} /> Итоги
        </button>
        <button onClick={onExpand}
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-pill bg-ink text-white text-[13px] font-display font-semibold hover:bg-[#2A2A2A]">
          <Sparkles size={14} /> Масштабировать
        </button>
      </div>
    </div>
  );
}

function ScriptChangesModal({ bloggerName, onClose, onSubmit }: {
  bloggerName: string; onClose: () => void; onSubmit: (message: string) => void;
}) {
  const [message, setMessage] = useState("");
  return (
    <div className="fixed inset-0 z-[120] grid place-items-center" style={{ background: "rgba(28,28,28,0.55)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-lg fade-up p-6 grid gap-4" style={{ width: "min(480px, 92vw)" }}>
        <h3 className="font-display font-extrabold text-[19px] tracking-tight">Вернуть сценарий на доработку</h3>
        <p className="text-[12.5px] text-ink-40 -mt-2">Сообщение уйдёт {bloggerName} в чат — опишите, что поправить в сценарии.</p>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Что нужно изменить в сценарии?"
          rows={4}
          autoFocus
          className="w-full px-3 py-2.5 rounded-md border border-ink-12 text-[13.5px] outline-none focus:border-ink resize-none"
        />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="h-9 px-4 rounded-pill border border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:border-ink/40">Отмена</button>
          <button onClick={() => message.trim() && onSubmit(message.trim())} disabled={!message.trim()}
            className="h-9 px-4 rounded-pill bg-ink text-white text-[13px] font-display font-semibold hover:bg-[#2A2A2A] disabled:opacity-50">
            Отправить на доработку
          </button>
        </div>
      </div>
    </div>
  );
}

function Board({ cols, placements, bloggers, onReplace, onScale, onWrite, onOpenDetail, onRepeat, onFindSimilar, onConfirm, onApproveScript, onRequestScriptChanges, onCancel, membership, onSaveToCollection }: {
  cols: { id: string; label: string }[]; placements: Placement[]; bloggers: Map<string, Blogger>;
  onReplace: (p: Placement) => void; onScale: () => void; onWrite: (bid: string) => void;
  onOpenDetail: (p: Placement) => void; onRepeat: (placementId: string) => void;
  onFindSimilar: () => void; onConfirm: (placementId: string, bloggerName: string) => void;
  onApproveScript: (placementId: string, bloggerName: string) => void;
  onRequestScriptChanges: (p: Placement) => void;
  onCancel: (placementId: string, bloggerName: string) => void;
  membership: Membership; onSaveToCollection: (bid: string, bname: string) => void;
}) {
  const declined = placements.filter((p) => p.status === "declined");
  const cancelled = placements.filter((p) => p.status === "cancelled");
  return (
    <div className="px-7 pt-2 pb-6 flex gap-3 overflow-x-auto">
      {cols.map((col) => {
        const items = placements.filter((p) => p.status === col.id);
        return (
          <div key={col.id} className="w-[260px] flex-shrink-0 flex flex-col gap-2.5">
            <div className="flex items-center justify-between px-1">
              <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-60">{col.label}</div>
              <span className="text-[11px] font-mono text-ink-40">{items.length}</span>
            </div>
            {items.map((p) => (
              <KanbanCard key={p.id} p={p} blogger={bloggers.get(p.blogger_id)} onScale={onScale} onWrite={onWrite} onOpenDetail={onOpenDetail} onRepeat={onRepeat} onFindSimilar={onFindSimilar} onConfirm={onConfirm} onApproveScript={onApproveScript} onRequestScriptChanges={onRequestScriptChanges} onCancel={onCancel} membership={membership} onSaveToCollection={onSaveToCollection} />
            ))}
            {items.length === 0 && (
              <div className="text-[12px] text-ink-40 text-center py-6">—</div>
            )}
          </div>
        );
      })}
      {declined.length > 0 && (
        <div className="w-[260px] flex-shrink-0 flex flex-col gap-2.5 p-2 rounded-md"
          style={{ background: "rgba(229,72,77,0.06)" }}>
          <div className="flex items-center justify-between px-1">
            <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-danger">Отказались · нужна замена</div>
            <span className="text-[11px] font-mono text-ink-40">{declined.length}</span>
          </div>
          {declined.map((p) => (
            <KanbanCard key={p.id} p={p} blogger={bloggers.get(p.blogger_id)} declined onReplace={onReplace} onScale={onScale} onWrite={onWrite} onOpenDetail={onOpenDetail} onRepeat={onRepeat} onFindSimilar={onFindSimilar} onConfirm={onConfirm} onApproveScript={onApproveScript} onRequestScriptChanges={onRequestScriptChanges} onCancel={onCancel} membership={membership} onSaveToCollection={onSaveToCollection} />
          ))}
        </div>
      )}
      {cancelled.length > 0 && (
        <div className="w-[260px] flex-shrink-0 flex flex-col gap-2.5 p-2 rounded-md bg-ink-6/40">
          <div className="flex items-center justify-between px-1">
            <div className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">Отменено рекламодателем</div>
            <span className="text-[11px] font-mono text-ink-40">{cancelled.length}</span>
          </div>
          {cancelled.map((p) => (
            <KanbanCard key={p.id} p={p} blogger={bloggers.get(p.blogger_id)} onScale={onScale} onWrite={onWrite} onOpenDetail={onOpenDetail} onRepeat={onRepeat} onFindSimilar={onFindSimilar} onConfirm={onConfirm} onApproveScript={onApproveScript} onRequestScriptChanges={onRequestScriptChanges} onCancel={onCancel} membership={membership} onSaveToCollection={onSaveToCollection} />
          ))}
        </div>
      )}
    </div>
  );
}

function KanbanCard({ p, blogger, declined, onReplace, onScale, onWrite, onOpenDetail, onRepeat, onFindSimilar, onConfirm, onApproveScript, onRequestScriptChanges, onCancel, membership, onSaveToCollection }: {
  p: Placement; blogger?: Blogger; declined?: boolean;
  onReplace?: (p: Placement) => void; onScale?: () => void; onWrite?: (bid: string) => void;
  onOpenDetail?: (p: Placement) => void; onRepeat?: (placementId: string) => void;
  onFindSimilar?: () => void; onConfirm?: (placementId: string, bloggerName: string) => void;
  onApproveScript?: (placementId: string, bloggerName: string) => void;
  onRequestScriptChanges?: (p: Placement) => void;
  onCancel?: (placementId: string, bloggerName: string) => void;
  membership?: Membership; onSaveToCollection?: (bid: string, bname: string) => void;
}) {
  if (!blogger) return null;
  const closed = p.status === "published" || p.status === "done";
  const isDone = p.status === "done";
  const isReview = p.status === "review";
  const isScriptReview = p.status === "script_review";
  const isCancelled = p.status === "cancelled";
  const winner = isDone && p.result && (p.top_performer || p.result.cpv < 0.5);
  const inAny = (membership?.[p.blogger_id]?.length ?? 0) > 0;

  // Wrap inner button clicks so they don't trigger the card's openDetail.
  const stop = (fn?: () => void) => (e: React.MouseEvent) => { e.stopPropagation(); fn?.(); };

  return (
    <div onClick={() => onOpenDetail?.(p)} className={cn(
      "bg-white rounded-md p-3 flex flex-col gap-2 border cursor-pointer transition-colors hover:border-ink/40",
      p.is_new ? "border-lime" : declined ? "border-danger/30" : "border-ink-12",
      p.top_performer && "shadow-sm",
    )}>
      {(p.top_performer || p.is_new || p.is_overdue) && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {p.top_performer && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-lime text-[var(--fg-on-accent)] text-[9px] font-mono font-bold tracking-[0.06em]">
              ★ Лучший результат
            </span>
          )}
          {p.is_new && !p.top_performer && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-lime/30 text-[#1A3A05] text-[9px] font-mono font-bold tracking-[0.06em]">
              Новый
            </span>
          )}
          {p.is_overdue && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-warning/20 text-[#7A4B00] text-[9px] font-mono font-bold tracking-[0.06em]">
              Просрочено
            </span>
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Avatar name={blogger.full} size={28} grad={blogger.grad as [string, string]} />
        <div className="flex items-center gap-1 font-display font-semibold text-[12.5px] truncate flex-1">
          <PlatformIcon platform={blogger.platform as Platform} size={12} /> {blogger.name}
        </div>
        <button onClick={stop(() => onSaveToCollection?.(blogger.id, blogger.name))}
          title="В подборку"
          className="w-6 h-6 grid place-items-center rounded-md flex-shrink-0 text-ink-40 hover:text-ink hover:bg-ink-6">
          {inAny
            ? <BookmarkCheck size={13} className="text-lime" />
            : <Bookmark size={13} />}
        </button>
      </div>

      {closed && p.result && (
        <div className="grid grid-cols-2 gap-1.5 p-2 bg-cream rounded-sm text-[11px]">
          <Stat k="Просмотры" v={fmtNum(p.result.views)} />
          <Stat k="CPV"       v={`${p.result.cpv}₽`} accent={p.result.cpv < 0.5} />
          <Stat k="CTR"       v={`${p.result.ctr}%`} />
          <Stat k="Конверсии" v={String(p.result.conv)} />
        </div>
      )}

      {!closed && p.msg && <div className="text-[12px] text-ink-60 line-clamp-2">{p.msg}</div>}

      {!declined && !closed && (
        <div className="text-[11px] text-ink-40 font-mono">{fmtNum(blogger.reach)} подп.</div>
      )}

      {!declined && (
        <div className="flex justify-between items-center text-[11.5px]">
          <span className="inline-flex items-center gap-1 text-ink-40"><Calendar size={11} /> {fmtDate(p.publish_date)}</span>
          <span className="font-mono font-semibold">{fmtRub(p.price)}</span>
        </div>
      )}

      {declined && (
        <div className="flex items-start gap-1.5 px-2 py-1.5 bg-danger/10 text-danger rounded-sm text-[11.5px]">
          <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
          <div>{p.decline_reason}</div>
        </div>
      )}

      {!declined && (
        <button onClick={stop(() => onWrite?.(blogger.id))}
          className="inline-flex items-center justify-center gap-1.5 h-8 px-2 rounded-pill border-2 border-ink-12 text-[11.5px] font-display font-semibold text-ink hover:border-ink">
          <MessageSquare size={11} /> Написать
        </button>
      )}

      {isCancelled && p.cancel_reason && (
        <div className="flex items-start gap-1.5 px-2 py-1.5 bg-ink-6 text-ink-60 rounded-sm text-[11.5px]">
          <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
          <div>{p.cancel_reason}</div>
        </div>
      )}

      {declined && (
        <div className="grid grid-cols-2 gap-1.5">
          <button onClick={stop(() => onWrite?.(blogger.id))}
            className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-pill border-2 border-ink-12 text-[11.5px] font-display font-semibold text-ink-60 hover:border-ink hover:text-ink">
            <MessageSquare size={11} /> Написать
          </button>
          <button onClick={stop(() => onReplace?.(p))}
            className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-pill bg-danger/10 text-danger text-[11.5px] font-display font-semibold hover:bg-danger/20">
            <RefreshCw size={11} /> Заменить канал
          </button>
        </div>
      )}

      {/* Any still-active placement can be pulled directly — the natural
          escape hatch for "advertiser cancels mid-negotiation" (esp. an
          overdue native script round) without needing to pick a replacement
          blogger first. */}
      {!declined && !isCancelled && !closed && !isReview && (
        <button onClick={stop(() => onCancel?.(p.id, blogger.name))}
          className="inline-flex items-center justify-center gap-1.5 h-7 px-2 rounded-pill text-[11px] font-display font-semibold text-ink-40 hover:text-danger hover:bg-danger/10">
          <X size={10} strokeWidth={3} /> Отменить размещение
        </button>
      )}

      {isScriptReview && (
        <div className="p-2.5 rounded-sm border border-ink-12 bg-cream grid gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-[0.06em] text-ink-60">
            <AlertCircle size={12} /> Сценарий на согласовании
          </div>
          {p.script_draft && <div className="text-[11.5px] text-ink-60 line-clamp-3">{p.script_draft}</div>}
          <div className="grid grid-cols-2 gap-1.5">
            <button onClick={stop(() => onApproveScript?.(p.id, blogger.name))}
              className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[11.5px] font-display font-semibold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]">
              <Check size={11} strokeWidth={3} /> Согласовать
            </button>
            <button onClick={stop(() => onRequestScriptChanges?.(p))}
              className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-pill border-2 border-ink-12 text-[11.5px] font-display font-semibold text-ink-60 hover:border-ink hover:text-ink">
              <MessageSquare size={11} /> На доработку
            </button>
          </div>
        </div>
      )}

      {isReview && (
        <div className="p-2.5 rounded-sm border border-ink-12 bg-cream grid gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-[0.06em] text-ink-60">
            <AlertCircle size={12} /> Отчёт на проверке
          </div>
          {p.report_link && (
            <a href={p.report_link} target="_blank" rel="noopener noreferrer"
              onClick={stop()}
              className="inline-flex items-center gap-1 text-[11.5px] text-link hover:underline truncate">
              <ExternalLink size={11} /> {p.report_link}
            </a>
          )}
          <button onClick={stop(() => onConfirm?.(p.id, blogger.name))}
            className="inline-flex items-center justify-center gap-1.5 h-8 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[11.5px] font-display font-semibold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]">
            <Check size={12} strokeWidth={3} /> Подтвердить и оплатить
          </button>
        </div>
      )}

      {winner && (
        <div className="p-2.5 rounded-sm border border-lime"
          style={{ background: "rgba(191,249,63,0.18)" }}>
          <div className="flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-[0.06em] mb-1">
            <TrendingUp size={13} /> Сработал лучше плана
          </div>
          <div className="text-[12px] text-ink-60 leading-snug mb-2">
            CPV <b className="text-success">{p.result!.cpv}₽</b> — не ждите остальных, масштабируйте сейчас:
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <button onClick={stop(() => onRepeat?.(p.id))} className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[11.5px] font-display font-semibold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)]">
              <Copy size={11} /> Повторить размещение
            </button>
            <button onClick={stop(onFindSimilar)}
              className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-pill border-2 border-ink-12 text-[11.5px] font-display font-semibold text-ink hover:border-ink">
              <Sparkles size={11} /> Найти похожие каналы
            </button>
          </div>
        </div>
      )}

      {isDone && !winner && (
        <div className="grid grid-cols-2 gap-1.5">
          <button onClick={stop(() => onRepeat?.(p.id))}
            className="inline-flex items-center justify-center gap-1 h-8 rounded-pill bg-lime/20 text-[#1A3A05] text-[11.5px] font-display font-semibold hover:bg-lime/30">
            <Copy size={11} /> Повторить размещение
          </button>
          <button onClick={stop(onFindSimilar)}
            className="inline-flex items-center justify-center gap-1 h-8 rounded-pill bg-ink-6 text-ink-60 text-[11.5px] font-display font-semibold hover:bg-ink-12 hover:text-ink">
            <Sparkles size={11} /> Похожие каналы
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ k, v, accent }: { k: string; v: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-[10px] text-ink-40">{k}</div>
      <div className={cn("font-mono font-bold text-[12.5px]", accent ? "text-success" : "text-ink")}>{v}</div>
    </div>
  );
}

function TableView({ placements, bloggers, columns, onOpenDetail, onReplace, onRepeat, onFindSimilar, onConfirm, onApproveScript, membership, onSaveToCollection, onWrite }: {
  placements: Placement[]; bloggers: Map<string, Blogger>; columns: { id: string; label: string }[];
  onOpenDetail: (p: Placement) => void;
  onReplace: (p: Placement) => void;
  onRepeat: (pid: string) => void;
  onFindSimilar: () => void;
  onConfirm: (placementId: string, bloggerName: string) => void;
  onApproveScript: (placementId: string, bloggerName: string) => void;
  membership: Membership; onSaveToCollection: (bid: string, bname: string) => void;
  onWrite: (bid: string) => void;
}) {
  return (
    <Card className="!p-0 overflow-hidden mx-7 mt-3 mb-12">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-[10.5px] uppercase tracking-wider text-ink-40 border-b border-ink-12">
            {["Блогер", "Площадка", "Статус", "Причина отказа", "Дата выхода", "Цена", "Просмотры", "CTR", "CPV", "Лиды", "", "", ""].map((h, i) => (
              <th key={i} className="text-left px-3 py-2.5 font-display font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {placements.map((p) => {
            const b = bloggers.get(p.blogger_id);
            if (!b) return null;
            const col = columns.find((c) => c.id === p.status);
            const inAny = (membership[b.id]?.length ?? 0) > 0;
            return (
              <tr key={p.id} onClick={() => onOpenDetail(p)} className="border-b border-ink-12/60 hover:bg-ink-6 cursor-pointer">
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <Avatar name={b.full} size={24} grad={b.grad as [string, string]} />
                    <div>
                      <div className="font-display font-semibold flex items-center gap-1.5">
                        {b.name}
                        {p.top_performer && (
                          <span className="text-[9px] font-mono font-bold bg-lime text-[var(--fg-on-accent)] px-1.5 py-0.5 rounded-sm whitespace-nowrap">★ Топ</span>
                        )}
                        {p.is_new && !p.top_performer && (
                          <span className="text-[9px] font-mono font-bold bg-lime/30 text-[#1A3A05] px-1.5 py-0.5 rounded-sm">Новый</span>
                        )}
                      </div>
                      <div className="text-[11px] text-ink-40">{fmtNum(b.reach)} подп. · {b.full}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5"><PlatformIcon platform={b.platform as Platform} size={14} /></td>
                <td className="px-3 py-2.5">
                  <span className={cn("px-2 py-0.5 rounded-pill text-[10.5px] font-display font-semibold uppercase tracking-wider",
                    p.status === "declined" || p.status === "cancelled" ? "bg-danger/15 text-danger"
                    : (p.status === "done" || p.status === "published") ? "bg-success/15 text-success"
                    : p.status === "script_review" ? "bg-warning/15 text-[#7A4B00]"
                    : "bg-ink-6 text-ink-60")}>
                    {p.status === "cancelled" ? "Отменено" : col?.label ?? p.status}
                  </span>
                  {p.is_overdue && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded-pill text-[9.5px] font-mono font-bold bg-warning/20 text-[#7A4B00] uppercase whitespace-nowrap">
                      Просрочено
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 max-w-[180px]">
                  {p.decline_reason
                    ? <span className="text-[12px] text-danger leading-snug line-clamp-2">{p.decline_reason}</span>
                    : <span className="text-ink-30">—</span>}
                </td>
                <td className="px-3 py-2.5 font-mono">{fmtDate(p.publish_date)}</td>
                <td className="px-3 py-2.5 font-mono font-semibold">{fmtRub(p.price)}</td>
                <td className="px-3 py-2.5 font-mono">{p.result ? fmtNum(p.result.views) : "—"}</td>
                <td className="px-3 py-2.5 font-mono">{p.result ? `${p.result.ctr}%` : "—"}</td>
                <td className={cn("px-3 py-2.5 font-mono", p.result && p.result.cpv < 0.5 && "text-success font-bold")}>
                  {p.result ? `${p.result.cpv}₽` : "—"}
                </td>
                <td className="px-3 py-2.5 font-mono">{p.result ? p.result.conv : "—"}</td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => onSaveToCollection(b.id, b.name)}
                    title="В подборку"
                    className="w-7 h-7 grid place-items-center rounded-md text-ink-40 hover:text-ink hover:bg-ink-6">
                    {inAny
                      ? <BookmarkCheck size={14} className="text-lime" />
                      : <Bookmark size={14} />}
                  </button>
                </td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => onWrite(b.id)}
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded-pill border border-ink-12 text-ink-60 text-[11.5px] font-display font-semibold hover:text-ink hover:border-ink/40 whitespace-nowrap"
                  >
                    <MessageSquare size={11} /> Написать
                  </button>
                </td>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  {p.status === "declined" && (
                    <button
                      onClick={() => onReplace(p)}
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-pill bg-danger/10 text-danger text-[11.5px] font-display font-semibold hover:bg-danger/20 whitespace-nowrap"
                    >
                      <RefreshCw size={11} /> Заменить
                    </button>
                  )}
                  {p.status === "review" && (
                    <button
                      onClick={() => onConfirm(p.id, b.name)}
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[11.5px] font-display font-semibold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] whitespace-nowrap"
                    >
                      <Check size={11} strokeWidth={3} /> Подтвердить
                    </button>
                  )}
                  {p.status === "script_review" && (
                    <button
                      onClick={() => onApproveScript(p.id, b.name)}
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-pill bg-lime text-[var(--fg-on-accent)] text-[11.5px] font-display font-semibold hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] whitespace-nowrap"
                    >
                      <Check size={11} strokeWidth={3} /> Согласовать сценарий
                    </button>
                  )}
                  {(p.status === "done" || p.status === "published") && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => onFindSimilar()}
                        title="Похожие каналы"
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-pill bg-ink-6 text-ink-60 text-[11.5px] font-display font-semibold hover:bg-ink-12 whitespace-nowrap"
                      >
                        <Sparkles size={11} /> Похожие каналы
                      </button>
                      <button
                        onClick={() => onRepeat(p.id)}
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-pill bg-lime/20 text-[#1A3A05] text-[11.5px] font-display font-semibold hover:bg-lime/30 whitespace-nowrap"
                      >
                        <Copy size={11} /> Повторить
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}


// ─── Add from collection modal ────────────────────────────────────────────────

function pluralBloggers(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "блогер";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return "блогера";
  return "блогеров";
}

function AddFromCollectionModal({ campaignId, existingBloggerIds, onClose }: {
  campaignId: string;
  existingBloggerIds: Set<string>;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<CollectionSummary | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const { data: collections } = useCollections();
  const { data: detail, isLoading: detailLoading } = useCollection(selected?.id ?? null);
  const { data: allBloggers } = useBloggers({});
  const extend = useExtendCampaignFromCollection();

  const byId = new Map((allBloggers ?? []).map((b) => [b.id, b]));
  const members: Blogger[] = (detail?.members ?? [])
    .map((m) => byId.get(m.blogger_id))
    .filter((b): b is Blogger => !!b);

  function toggle(bid: string) {
    if (existingBloggerIds.has(bid)) return;
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(bid) ? next.delete(bid) : next.add(bid);
      return next;
    });
  }

  async function addPicked() {
    if (!selected || picked.size === 0) return;
    await extend.mutateAsync({ collectionId: selected.id, campaignId, bloggerIds: [...picked] });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center"
      style={{ background: "rgba(28,28,28,0.50)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg)] rounded-lg shadow-lg w-[min(520px,92vw)] fade-up flex flex-col max-h-[80vh]">

        <div className="px-6 pt-5 pb-4 border-b border-ink-12 flex-shrink-0">
          {selected && (
            <button onClick={() => { setSelected(null); setPicked(new Set()); }}
              className="flex items-center gap-1 text-[12px] text-ink-40 hover:text-ink mb-2">
              <ArrowLeft size={13} /> Все подборки
            </button>
          )}
          <div className="font-display font-bold text-[18px]">
            {selected ? selected.name : "Добавить из подборки"}
          </div>
          {selected && (
            <div className="text-[12px] text-ink-40 mt-0.5">Выберите блогеров для добавления в кампанию</div>
          )}
        </div>

        <div className="overflow-y-auto flex-1 p-2">
          {!selected && (
            <>
              {(collections?.length ?? 0) === 0 && (
                <div className="text-center text-ink-40 py-10 text-[13px]">Нет подборок — создайте в разделе Подборки.</div>
              )}
              {(collections ?? []).map((c) => (
                <button key={c.id}
                  onClick={() => { setSelected(c); setPicked(new Set()); }}
                  className="flex items-center gap-3 w-full px-4 py-3 rounded-md text-left hover:bg-ink-6 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-[14px] truncate">{c.name}</div>
                    <div className="text-[12px] text-ink-40 mt-0.5">
                      {c.members_count} {pluralBloggers(c.members_count)}
                      {c.is_active && <span className="ml-2 text-lime font-semibold">· активная</span>}
                    </div>
                  </div>
                  <span className="text-ink-40 text-[12px]">→</span>
                </button>
              ))}
            </>
          )}

          {selected && (
            <>
              {detailLoading && (
                <div className="grid gap-1 p-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3">
                      <Skeleton className="w-5 h-5 rounded-sm flex-shrink-0" />
                      <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
                      <div className="flex-1 flex flex-col gap-1.5">
                        <Skeleton className="h-3.5 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!detailLoading && members.map((b) => {
                const inCamp = existingBloggerIds.has(b.id);
                const isSelected = picked.has(b.id);
                return (
                  <button key={b.id}
                    onClick={() => toggle(b.id)}
                    disabled={inCamp}
                    className={cn(
                      "flex items-center gap-3 w-full px-4 py-3 rounded-md text-left transition-colors",
                      inCamp ? "opacity-50 cursor-default" : "hover:bg-ink-6",
                    )}>
                    <div className={cn(
                      "w-5 h-5 rounded-sm border-2 grid place-items-center flex-shrink-0 transition-colors",
                      inCamp || isSelected ? "bg-lime border-lime" : "border-ink-12",
                    )}>
                      {(inCamp || isSelected) && (
                        <Check size={11} strokeWidth={3} className="text-[var(--fg-on-accent)]" />
                      )}
                    </div>
                    <Avatar name={b.full} size={32} grad={b.grad as [string, string]} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 font-display font-semibold text-[13.5px]">
                        <PlatformIcon platform={b.platform as Platform} size={11} /> {b.name}
                      </div>
                      <div className="text-[11.5px] text-ink-40 truncate">
                        {b.full} · охват {fmtNum(b.reach)}
                        {inCamp && <span className="ml-2 text-lime">уже в кампании</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>

        {selected && !detailLoading && (
          <div className="px-6 py-4 border-t border-ink-12 flex items-center justify-between gap-3 flex-shrink-0">
            <span className="text-[12.5px] text-ink-40">
              {picked.size > 0 ? `Выбрано: ${picked.size} ${pluralBloggers(picked.size)}` : "Нет выбранных"}
            </span>
            <div className="flex gap-2">
              <button onClick={onClose}
                className="h-9 px-4 rounded-pill border-2 border-ink-12 text-[13px] font-display font-semibold text-ink-60 hover:border-ink">
                Отмена
              </button>
              <button onClick={addPicked} disabled={picked.size === 0 || extend.isPending}
                className="h-9 px-4 rounded-pill bg-ink text-white text-[13px] font-display font-semibold hover:bg-[#2A2A2A] disabled:opacity-40">
                {extend.isPending ? "Добавляем…" : `Добавить${picked.size > 0 ? ` ${picked.size}` : ""}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KanbanSkeleton() {
  return (
    <div className="flex flex-col">
      <div className="px-7 pt-7 pb-5 flex items-start justify-between">
        <div className="flex flex-col gap-2.5">
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="flex gap-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-9 w-28 rounded-pill" />)}
          <Skeleton className="h-9 w-36 rounded-pill" />
        </div>
      </div>
      <Skeleton className="mx-7 h-[76px] rounded-lg" />
      <div className="px-7 pt-5 pb-6 flex gap-3 overflow-x-hidden">
        {[3, 2, 2, 1].map((count, col) => (
          <div key={col} className="w-[260px] flex-shrink-0 flex flex-col gap-2.5">
            <div className="flex items-center justify-between px-1">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-4" />
            </div>
            {Array.from({ length: count }).map((_, j) => (
              <div key={j} className="bg-white rounded-md p-3 border border-ink-12 flex flex-col gap-2.5">
                <div className="flex items-center gap-2">
                  <Skeleton className="w-7 h-7 rounded-full flex-shrink-0" />
                  <Skeleton className="h-4 flex-1" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-3 w-12" />
                </div>
                <Skeleton className="h-7 rounded-pill" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
